import * as React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCredentials, type Credential } from '../state/credential-state';
import { useBreadcrumb } from '../state/breadcrumb-state';
import * as api from '../services/api-client';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { ErrorState } from '../components/ui/error-state';
import { ResolvedUserName } from '../components/users/ResolvedUserName';

/**
 * CredentialDetailPage — Displays credential metadata and rotation history.
 * Fetches extended credential detail (including rotation history) from the API.
 * Accessible at /admin-console/credentials/:credentialId.
 *
 * Validates: Requirements 4.6, 4.7, 10.2, 10.4
 */

// ─── Types ───────────────────────────────────────────────────────────────────

interface RotationHistoryEntry {
  readonly rotatedAt: string;
  readonly initiatedBy: string;
  readonly outcome: 'success' | 'failed';
}

interface CredentialDetailResponse {
  readonly credential?: Credential & {
    readonly rotationHistory?: readonly RotationHistoryEntry[];
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export function CredentialDetailPage(): React.JSX.Element {
  const { credentialId } = useParams<{ credentialId: string }>();
  const { state: credentialState, loadCredentials } = useCredentials();
  const { setSegments } = useBreadcrumb();

  const [rotationHistory, setRotationHistory] = React.useState<readonly RotationHistoryEntry[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = React.useState(true);
  const [detailError, setDetailError] = React.useState<string | null>(null);
  const [isNotFound, setIsNotFound] = React.useState(false);

  // Load credentials list if not already loaded
  React.useEffect(() => {
    if (credentialState.credentials.length === 0 && !credentialState.isLoading) {
      loadCredentials();
    }
  }, [credentialState.credentials.length, credentialState.isLoading, loadCredentials]);

  // Find credential from state
  const credential = React.useMemo(
    () => credentialState.credentials.find((c) => c.credentialId === credentialId) ?? null,
    [credentialState.credentials, credentialId],
  );

  // Fetch credential detail (including rotation history) from API
  React.useEffect(() => {
    if (!credentialId) return;

    let cancelled = false;

    const fetchDetail = async (): Promise<void> => {
      setIsLoadingDetail(true);
      setDetailError(null);
      setIsNotFound(false);

      try {
        const resp = await api.get<CredentialDetailResponse>(`/admin/credentials/${credentialId}`);
        if (!cancelled) {
          if (resp.credential?.rotationHistory) {
            setRotationHistory(resp.credential.rotationHistory);
          } else {
            setRotationHistory([]);
          }
          setIsLoadingDetail(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof api.ApiErrorResponse && err.status === 404) {
          setIsNotFound(true);
        } else {
          setDetailError(err instanceof Error ? err.message : 'Failed to load credential details');
        }
        setIsLoadingDetail(false);
      }
    };

    fetchDetail();
    return () => { cancelled = true; };
  }, [credentialId]);

  // Set breadcrumb segments
  React.useEffect(() => {
    if (credential) {
      setSegments([
        { label: 'Credentials', href: '/admin-console/credentials' },
        { label: credential.description || `Credential ${credential.credentialId.slice(0, 8)}` },
      ]);
    }
    return () => {
      setSegments([]);
    };
  }, [credential, setSegments]);

  const handleRetry = React.useCallback((): void => {
    if (credentialId) {
      setIsLoadingDetail(true);
      setDetailError(null);
      setIsNotFound(false);

      const fetchDetail = async (): Promise<void> => {
        try {
          const resp = await api.get<CredentialDetailResponse>(`/admin/credentials/${credentialId}`);
          if (resp.credential?.rotationHistory) {
            setRotationHistory(resp.credential.rotationHistory);
          } else {
            setRotationHistory([]);
          }
          setIsLoadingDetail(false);
        } catch (err) {
          if (err instanceof api.ApiErrorResponse && err.status === 404) {
            setIsNotFound(true);
          } else {
            setDetailError(err instanceof Error ? err.message : 'Failed to load credential details');
          }
          setIsLoadingDetail(false);
        }
      };

      fetchDetail();
    }
    loadCredentials();
  }, [credentialId, loadCredentials]);

  // Loading state
  if (credentialState.isLoading || isLoadingDetail) {
    return <Spinner label="Loading credential details..." />;
  }

  // Error state
  if (detailError) {
    return <ErrorState message={detailError} onRetry={handleRetry} />;
  }

  if (credentialState.error && !credential) {
    return <ErrorState message={credentialState.error} onRetry={handleRetry} />;
  }

  // 404 state
  if (isNotFound || !credential) {
    return (
      <section aria-labelledby="credential-not-found-heading" className="flex flex-col items-center gap-4 py-12">
        <h2 id="credential-not-found-heading" className="text-xl font-semibold text-foreground">
          Credential Not Found
        </h2>
        <p className="text-sm text-muted-foreground">
          The credential you are looking for does not exist or has been removed.
        </p>
        <Link
          to="/admin-console/credentials"
          className="text-sm font-medium text-primary underline hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          ← Back to Credentials
        </Link>
      </section>
    );
  }

  const formattedValidatedAt = new Date(credential.validatedAt).toLocaleDateString();

  return (
    <section aria-labelledby="credential-detail-heading">
      {/* Credential Metadata Header */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 id="credential-detail-heading" className="text-2xl font-semibold text-foreground">
                {credential.description || `Credential ${credential.credentialId.slice(0, 8)}`}
              </h2>
              <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Domains:</dt>
                  <dd>{credential.domains.join(', ')}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Status:</dt>
                  <dd>
                    <Badge variant={credential.status === 'active' ? 'success' : 'warning'}>
                      {credential.status}
                    </Badge>
                  </dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Last validated:</dt>
                  <dd>{formattedValidatedAt}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <dt className="text-muted-foreground">Created by:</dt>
                  <dd><ResolvedUserName userId={credential.createdBy} /></dd>
                </div>
              </dl>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rotation History Section */}
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-foreground">Rotation History</h3>
      </div>

      {rotationHistory.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No rotation history available for this credential.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Date</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Initiator</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Outcome</th>
              </tr>
            </thead>
            <tbody>
              {rotationHistory.map((entry, index) => (
                <tr key={`${entry.rotatedAt}-${index}`} className="border-b">
                  <td className="px-4 py-3">{new Date(entry.rotatedAt).toLocaleString()}</td>
                  <td className="px-4 py-3">{entry.initiatedBy}</td>
                  <td className="px-4 py-3">
                    <Badge variant={entry.outcome === 'success' ? 'success' : 'destructive'}>
                      {entry.outcome}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
